import { Doctor, Staff, ScheduleItem, getDoctorPreferences } from './adminService';

export interface DAAssignment {
  staffId: string;
  doctorId: string;
  timeSlot: string;
  date: string;
  /** 1-4 = doctor's preference rank; null = picked by staff priority fallback. */
  preferenceRank: 1 | 2 | 3 | 4 | null;
}

/**
 * True when a schedule row covers the given date. Multi-day leaves are stored
 * as ONE row spanning date..endDate — checking `item.date === date` alone made
 * days 2..N of a leave invisible to every availability check, so staff on
 * leave still got auto-assigned (Khine, 9 Jul).
 */
const coversDate = (item: ScheduleItem, date: string): boolean => {
  const end = item.endDate && item.endDate > item.date ? item.endDate : item.date;
  return item.date <= date && date <= end;
};

export interface ConflictInfo {
  type: 'main_nurse_conflict' | 'double_assignment' | 'unavailable_assignment';
  description: string;
  affectedStaffId: string;
  affectedDoctors: string[];
}

export interface AssignmentResult {
  assignment: DAAssignment | null;
  reason?: string;
  alternativeSuggestions?: {
    staffId: string;
    staffName: string;
    preferenceRank: 1 | 2 | 3 | 4;
  }[];
  decisionTrail?: {
    staffId: string;
    staffName: string;
    preferenceRank: 1 | 2 | 3 | 4 | null;
    outcome: 'selected' | 'on_leave' | 'occupied' | 'inactive' | 'missing_staff';
    reason: string;
  }[];
}

/**
 * Checks if a DA is available at a specific time
 */
export const isDAAvailable = (
  staffId: string,
  timeSlot: string,
  date: string,
  scheduleItems: ScheduleItem[]
): boolean => {
  const conflictingItems = scheduleItems.filter(item =>
    coversDate(item, date) &&
    item.staffId === staffId &&
    item.startTime <= timeSlot &&
    item.endTime > timeSlot
  );

  return conflictingItems.length === 0;
};

/**
 * Checks if a DA is on leave or MC for the entire day
 */
export const isDAOnLeave = (
  staffId: string,
  date: string,
  scheduleItems: ScheduleItem[]
): boolean => {
  const leaveItems = scheduleItems.filter(item =>
    coversDate(item, date) &&
    item.staffId === staffId &&
    (item.type === 'leave' || item.type === 'mc')
  );

  return leaveItems.length > 0;
};

/**
 * Gets the currently assigned DA for a doctor at a specific time
 */
export const getAssignedDA = (
  doctorId: string,
  timeSlot: string,
  date: string,
  scheduleItems: ScheduleItem[]
): string | null => {
  const assignment = scheduleItems.find(item =>
    coversDate(item, date) &&
    item.doctorId === doctorId &&
    item.staffId &&
    item.startTime <= timeSlot &&
    item.endTime > timeSlot
  );

  return assignment?.staffId || null;
};

/**
 * Automatic DA assignment based on 4-tier priority system
 */
export const assignDAToDoctor = async (
  doctorId: string,
  timeSlot: string,
  date: string,
  scheduleItems: ScheduleItem[],
  staff: Staff[],
  endTime?: string
): Promise<AssignmentResult> => {
  try {
    // Get doctor's preferences
    const preferences = await getDoctorPreferences(doctorId);

    const alternatives: AssignmentResult['alternativeSuggestions'] = [];
    const decisionTrail: NonNullable<AssignmentResult['decisionTrail']> = [];
    const assignmentEndTime = endTime || timeSlot;

    // Try each preference rank in order
    for (let rank = 1; rank <= 4; rank++) {
      const preference = preferences.find(p => p.preference_rank === rank);
      
      if (!preference) continue;

      const staffMember = staff.find(s => s.id === preference.staff_id);
      if (!staffMember) {
        decisionTrail.push({
          staffId: preference.staff_id,
          staffName: 'Unknown DA',
          preferenceRank: rank as 1 | 2 | 3 | 4,
          outcome: 'missing_staff',
          reason: 'Staff record is missing',
        });
        continue;
      }

      const staffName = staffMember.short_name || staffMember.name;

      if (staffMember.status !== 'active') {
        alternatives.push({
          staffId: preference.staff_id,
          staffName,
          preferenceRank: rank as 1 | 2 | 3 | 4,
        });
        decisionTrail.push({
          staffId: preference.staff_id,
          staffName,
          preferenceRank: rank as 1 | 2 | 3 | 4,
          outcome: 'inactive',
          reason: 'Staff is inactive',
        });
        continue;
      }

      // Check if DA is on leave for the day
      if (isDAOnLeave(preference.staff_id, date, scheduleItems)) {
        alternatives.push({
          staffId: preference.staff_id,
          staffName,
          preferenceRank: rank as 1 | 2 | 3 | 4
        });
        decisionTrail.push({
          staffId: preference.staff_id,
          staffName,
          preferenceRank: rank as 1 | 2 | 3 | 4,
          outcome: 'on_leave',
          reason: `On leave/MC on ${date}`,
        });
        continue;
      }

      // Check if DA is available within the appointment range
      const conflictingAssignment = scheduleItems.find(item =>
        coversDate(item, date) &&
        item.staffId === preference.staff_id &&
        (
          endTime
            ? item.startTime < assignmentEndTime && item.endTime > timeSlot
            : item.startTime <= timeSlot && item.endTime > timeSlot
        )
      );

      if (!conflictingAssignment) {
        decisionTrail.push({
          staffId: preference.staff_id,
          staffName,
          preferenceRank: rank as 1 | 2 | 3 | 4,
          outcome: 'selected',
          reason: rank === 1
            ? 'Primary nurse is available'
            : `Backup selected because higher preferences were unavailable`,
        });

        const skippedDecisions = decisionTrail
          .filter(decision => decision.outcome !== 'selected')
          .map(decision => `#${decision.preferenceRank} ${decision.staffName}: ${decision.reason}`);

        const decisionSummary = skippedDecisions.length > 0
          ? `Assigned ${staffName} (Preference #${rank}) after fallback. ${skippedDecisions.join('; ')}.`
          : `Assigned ${staffName} as primary nurse (Preference #${rank}).`;

        return {
          assignment: {
            staffId: preference.staff_id,
            doctorId,
            timeSlot,
            date,
            preferenceRank: rank as 1 | 2 | 3 | 4
          },
          reason: decisionSummary,
          decisionTrail,
        };
      }

      // Add to alternatives list
      alternatives.push({
        staffId: preference.staff_id,
        staffName,
        preferenceRank: rank as 1 | 2 | 3 | 4
      });
      decisionTrail.push({
        staffId: preference.staff_id,
        staffName,
        preferenceRank: rank as 1 | 2 | 3 | 4,
        outcome: 'occupied',
        reason: `Already assigned from ${conflictingAssignment.startTime} to ${conflictingAssignment.endTime}`,
      });
    }

    // Staff-priority fallback (Khine 9 Jul: "auto assignment doesn't work
    // according to the priority setting"): when the doctor has no preference
    // list, or every preferred DA is unavailable, assign the available active
    // DA with the best (lowest) staff priority instead of giving up.
    const preferredIds = new Set(preferences.map(p => p.staff_id));
    const fallbackPool = staff
      .filter(s =>
        s.role?.toLowerCase().includes('dental assistant') &&
        s.status === 'active' &&
        !preferredIds.has(s.id)
      )
      .sort((a, b) => (a.priority || 999) - (b.priority || 999));

    for (const candidate of fallbackPool) {
      if (isDAOnLeave(candidate.id, date, scheduleItems)) continue;
      const busy = scheduleItems.find(item =>
        coversDate(item, date) &&
        item.staffId === candidate.id &&
        (
          endTime
            ? item.startTime < assignmentEndTime && item.endTime > timeSlot
            : item.startTime <= timeSlot && item.endTime > timeSlot
        )
      );
      if (busy) continue;

      const candidateName = candidate.short_name || candidate.name;
      const fallbackContext = preferences.length === 0
        ? 'no preference list for this doctor'
        : 'all preferred DAs unavailable';
      decisionTrail.push({
        staffId: candidate.id,
        staffName: candidateName,
        preferenceRank: null,
        outcome: 'selected',
        reason: `Assigned by staff priority (${fallbackContext})`,
      });
      return {
        assignment: {
          staffId: candidate.id,
          doctorId,
          timeSlot,
          date,
          preferenceRank: null,
        },
        reason: `Assigned ${candidateName} by staff priority (${fallbackContext}).`,
        decisionTrail,
      };
    }

    const trailSummary = decisionTrail
      .map(decision => `#${decision.preferenceRank ?? 'P'} ${decision.staffName}: ${decision.reason}`)
      .join('; ');

    return {
      assignment: null,
      reason: trailSummary
        ? `No preferred or priority-listed DAs available at this time. ${trailSummary}.`
        : 'No DAs available at this time (none configured or all on leave/occupied)',
      alternativeSuggestions: alternatives,
      decisionTrail,
    };

  } catch (error) {
    console.error('Error in DA assignment:', error);
    return {
      assignment: null,
      reason: 'Error loading doctor preferences'
    };
  }
};

/**
 * Detects conflicts in DA assignments and preferences
 */
export const detectConflicts = async (
  doctors: Doctor[],
  staff: Staff[],
  scheduleItems: ScheduleItem[],
  date: string
): Promise<ConflictInfo[]> => {
  const conflicts: ConflictInfo[] = [];

  try {
    // Get all doctor preferences
    const allPreferences = await Promise.all(
      doctors.map(async (doctor) => {
        const prefs = await getDoctorPreferences(doctor.id);
        return { doctorId: doctor.id, preferences: prefs };
      })
    );

    // Check for main nurse conflicts (multiple doctors having same DA as rank 1)
    const mainNurseAssignments: Record<string, string[]> = {};
    
    allPreferences.forEach(({ doctorId, preferences }) => {
      const mainNurse = preferences.find(p => p.preference_rank === 1);
      if (mainNurse) {
        if (!mainNurseAssignments[mainNurse.staff_id]) {
          mainNurseAssignments[mainNurse.staff_id] = [];
        }
        mainNurseAssignments[mainNurse.staff_id].push(doctorId);
      }
    });

    Object.entries(mainNurseAssignments).forEach(([staffId, doctorIds]) => {
      if (doctorIds.length > 1) {
        const staffMember = staff.find(s => s.id === staffId);
        conflicts.push({
          type: 'main_nurse_conflict',
          description: `${staffMember?.short_name || staffMember?.name || 'Unknown DA'} is assigned as main nurse to ${doctorIds.length} doctors`,
          affectedStaffId: staffId,
          affectedDoctors: doctorIds
        });
      }
    });

    // Check for double assignments (DA assigned to multiple doctors at same time)
    const dateSchedules = scheduleItems.filter(item => coversDate(item, date));
    const timeSlots = new Set(dateSchedules.map(item => item.startTime));

    timeSlots.forEach(timeSlot => {
      const assignmentsAtTime: Record<string, string[]> = {};
      
      dateSchedules.forEach(item => {
        if (item.staffId && 
            item.startTime <= timeSlot && 
            item.endTime > timeSlot &&
            item.doctorId) {
          if (!assignmentsAtTime[item.staffId]) {
            assignmentsAtTime[item.staffId] = [];
          }
          assignmentsAtTime[item.staffId].push(item.doctorId);
        }
      });

      Object.entries(assignmentsAtTime).forEach(([staffId, doctorIds]) => {
        if (doctorIds.length > 1) {
          const staffMember = staff.find(s => s.id === staffId);
          conflicts.push({
            type: 'double_assignment',
            description: `${staffMember?.short_name || staffMember?.name || 'Unknown DA'} is assigned to multiple doctors at ${timeSlot}`,
            affectedStaffId: staffId,
            affectedDoctors: doctorIds
          });
        }
      });
    });

    // Check for assignments to unavailable DAs (on leave/MC)
    dateSchedules.forEach(item => {
      if (item.staffId && item.doctorId) {
        const isOnLeave = isDAOnLeave(item.staffId, date, scheduleItems);
        if (isOnLeave) {
          const staffMember = staff.find(s => s.id === item.staffId);
          conflicts.push({
            type: 'unavailable_assignment',
            description: `${staffMember?.short_name || staffMember?.name || 'Unknown DA'} is assigned but on leave/MC`,
            affectedStaffId: item.staffId,
            affectedDoctors: [item.doctorId]
          });
        }
      }
    });

    return conflicts;

  } catch (error) {
    console.error('Error detecting conflicts:', error);
    return [];
  }
};

/**
 * Suggests alternative DAs when preferred ones are unavailable
 */
export const suggestAlternativeDA = async (
  doctorId: string,
  timeSlot: string,
  date: string,
  scheduleItems: ScheduleItem[],
  staff: Staff[]
): Promise<{ staffId: string; staffName: string; reason: string }[]> => {
  const dentalAssistants = staff.filter(s => 
    s.role.toLowerCase().includes('dental assistant') && s.status === 'active'
  );

  const suggestions: { staffId: string; staffName: string; reason: string }[] = [];

  for (const staffMember of dentalAssistants) {
    // Skip if on leave
    if (isDAOnLeave(staffMember.id, date, scheduleItems)) {
      continue;
    }

    // Check availability
    if (isDAAvailable(staffMember.id, timeSlot, date, scheduleItems)) {
      suggestions.push({
        staffId: staffMember.id,
        staffName: staffMember.short_name || staffMember.name,
        reason: 'Available and not assigned'
      });
    }
  }

  // Sort by staff priority (lower number = higher priority)
  suggestions.sort((a, b) => {
    const staffA = staff.find(s => s.id === a.staffId);
    const staffB = staff.find(s => s.id === b.staffId);
    return (staffA?.priority || 999) - (staffB?.priority || 999);
  });

  return suggestions.slice(0, 5); // Return top 5 suggestions
};

/**
 * Gets comprehensive backup coverage analysis for a doctor
 */
export const getBackupCoverage = async (
  doctorId: string,
  date: string,
  scheduleItems: ScheduleItem[],
  staff: Staff[]
): Promise<{
  hasMainNurse: boolean;
  hasBackupOptions: boolean;
  coveragePercentage: number;
  gaps: string[];
}> => {
  try {
    const preferences = await getDoctorPreferences(doctorId);
    
    const coverage = {
      hasMainNurse: false,
      hasBackupOptions: false,
      coveragePercentage: 0,
      gaps: [] as string[]
    };

    if (preferences.length === 0) {
      coverage.gaps.push('No preferences configured');
      return coverage;
    }

    // Check main nurse (rank 1)
    const mainNurse = preferences.find(p => p.preference_rank === 1);
    if (mainNurse && !isDAOnLeave(mainNurse.staff_id, date, scheduleItems)) {
      coverage.hasMainNurse = true;
    } else {
      coverage.gaps.push('Main nurse not available');
    }

    // Check backup options (ranks 2-4)
    const backupOptions = preferences.filter(p => p.preference_rank > 1);
    const availableBackups = backupOptions.filter(p => 
      !isDAOnLeave(p.staff_id, date, scheduleItems)
    );

    coverage.hasBackupOptions = availableBackups.length > 0;
    coverage.coveragePercentage = (availableBackups.length / Math.max(backupOptions.length, 1)) * 100;

    if (availableBackups.length === 0) {
      coverage.gaps.push('No backup DAs available');
    } else if (availableBackups.length < backupOptions.length) {
      coverage.gaps.push(`${backupOptions.length - availableBackups.length} backup DAs on leave`);
    }

    return coverage;

  } catch (error) {
    console.error('Error analyzing backup coverage:', error);
    return {
      hasMainNurse: false,
      hasBackupOptions: false,
      coveragePercentage: 0,
      gaps: ['Error loading preferences']
    };
  }
};
